import { useState } from 'react';
import { ActivityClient, ActivityDTO, ApiResponse_string, UpdateActivityStatusDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

export const useActivityApi = () => {
    const apiClient = new ActivityClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: ActivityDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createActivity(data);
        } catch (err: any) {
            setError(err.message || 'Error creating Activity');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: ActivityDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateActivity(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating Activity');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getActivityById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching Activity');
        } finally {
            setIsLoading(false);
        }
    };

    const getComplianceActivities = async () => {
        setIsLoading(true);
        try {
            return await apiClient.getComplianceActivities();
        } catch (err: any) {
            setError(err.message || 'Error fetching Compliance Activities');
        } finally {
            setIsLoading(false);
        }
    };

    const getComplianceActivitiesHistory = async (numberOfYears: number = 6) => {
        setIsLoading(true);
        try {
            return await apiClient.getComplianceActivitiesHistory(numberOfYears);
        } catch (err: any) {
            setError(err.message || 'Error fetching Compliance Activities History');
        } finally {
            setIsLoading(false);
        }
    };

    const getAllComplianceActivitiesHistory = async (numberOfYears: number = 0) => {
        setIsLoading(true);
        try {
            return await apiClient.getAllComplianceActivitiesHistory(numberOfYears);
        } catch (err: any) {
            setError(err.message || 'Error fetching All Compliance Activities History');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchActivities(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching Activities');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteActivity(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting Activity');
        } finally {
            setIsLoading(false);
        }
    };

    const restoreFromDeleted = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.restoreFromDeletedActivity(id);
        } catch (err: any) {
            setError(err.message || 'Error restoring Activity from deleted state');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportActivities(filters);
        } catch (err: unknown) {
            handleHookError(err, 'Error exporting Activities', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getReport = async (id: string): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.generateActivityReportPdf(id);
        } catch (err: unknown) {
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getSportShootingListPdf = async (memberId: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.generateSportShootingListPdf(memberId);
            return response.data || '';
        } catch (err: unknown) {
            handleHookError(err, 'Error generating sport shooting list PDF', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getSportShootingListHtml = async (memberId: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.generateSportShootingListHtml(memberId);
            return response.data || '';
        } catch (err: unknown) {
            handleHookError(err, 'Error generating sport shooting list HTML', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getActivityReportHtml = async (memberId: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.generateActivityReportHtml(memberId);
            return response.data || '';
        } catch (err: unknown) {
            handleHookError(err, 'Error generating activity report HTML', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateStatus = async (data: UpdateActivityStatusDTO) => {
        setIsLoading(true);
        try {
            return await apiClient.updateActivityQuickStatus(data);
        } catch (err: any) {
            setError(err.message || 'Error updating activity status');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };


    const getFeedback = async (id: string): Promise<string | undefined> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getActivityFeedback(id);
            return response.data ?? undefined;
        } catch (err: any) {
            setError(err.message || 'Error fetching Activity feedback');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getDistinctYears = async (): Promise<number[]> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getActivityYears();
            return (response.data as unknown as number[]) ?? [];
        } catch (err: any) {
            setError(err.message || 'Error fetching activity years');
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, getComplianceActivities, getComplianceActivitiesHistory, getAllComplianceActivitiesHistory, search, restoreFromDeleted, extract, remove, getReport, getSportShootingListPdf, getSportShootingListHtml, getActivityReportHtml, updateStatus, getDistinctYears, getFeedback, isLoading, error };
};